import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft } from 'lucide-react';
import LandingHeader from './landing/LandingHeader';
import LandingFooter from './landing/LandingFooter';
import WhatsAppButton from './WhatsAppButton';
import ChatAssistant from './ChatAssistant';
import ShareButtons from './ShareButtons';
import { PostCard, CategoryBadge, formatPostDate } from './landing/BlogSection';
import { API, trackClick, trackShare } from './landing/constants';

const setMeta = (title, description) => {
  document.title = title;
  let tag = document.querySelector('meta[name="description"]');
  if (!tag) { tag = document.createElement('meta'); tag.name = 'description'; document.head.appendChild(tag); }
  tag.content = description;
};

const Shell = ({ children, article }) => (
  <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
    <LandingHeader />
    {children}
    <LandingFooter />
    <WhatsAppButton onClick={() => trackClick('WhatsApp')} />
    <ChatAssistant article={article} />
  </div>
);

export const BlogListPage = () => {
  const [posts, setPosts] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [params, setParams] = useSearchParams();
  const categoria = params.get('categoria') || '';
  useEffect(() => {
    setMeta('Blog de consejos de viaje y seguros | IwannaInsurance', 'Artículos cortos sobre seguros de viaje, visas Schengen, coberturas médicas y consejos para viajar protegido.');
    setPosts(null);
    axios.get(`${API}/blog`, { params: categoria ? { categoria } : {} }).then(({ data }) => { setPosts(data.data); setCategorias(data.categorias); }).catch(() => setPosts([]));
  }, [categoria]);
  const chip = (value, label) => (
    <button key={value} type="button" data-testid={`blog-filter-${value || 'todos'}`} onClick={() => setParams(value ? { categoria: value } : {})}
      className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${categoria === value ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'}`}>{label}</button>
  );
  return (
    <Shell>
      <main className="container mx-auto px-4 py-16" data-testid="blog-list-page">
        <Link to="/" data-testid="blog-back-home" className="inline-flex items-center text-slate-500 hover:text-blue-600 text-sm mb-6"><ArrowLeft className="h-4 w-4 mr-1" /> Volver al inicio</Link>
        <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-3">Blog de consejos</h1>
        <p className="text-lg text-slate-600 mb-8 max-w-2xl">Guías cortas para viajar protegido, entender tu póliza y elegir al aliado correcto.</p>
        <div className="flex flex-wrap gap-2 mb-10" data-testid="blog-filters">{chip('', 'Todos')}{categorias.map((c) => chip(c, c))}</div>
        {posts === null && <p className="text-slate-500">Cargando...</p>}
        {posts && posts.length === 0 && <p className="text-slate-500" data-testid="blog-empty">No hay artículos en esta categoría todavía.</p>}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(posts || []).map((p, i) => <PostCard key={p.id} post={p} index={i} />)}
        </div>
      </main>
    </Shell>
  );
};

export const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relacionados, setRelacionados] = useState([]);
  const [error, setError] = useState(false);
  useEffect(() => {
    setPost(null); setError(false);
    axios.get(`${API}/blog/${slug}`).then(({ data }) => {
      setPost(data.data);
      setRelacionados(data.relacionados || []);
      setMeta(`${data.data.titulo} | IwannaInsurance`, data.data.resumen);
      window.scrollTo(0, 0);
      axios.post(`${API}/blog/${slug}/view`).catch(() => {});
    }).catch(() => setError(true));
  }, [slug]);
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  return (
    <Shell article={post ? { slug: post.slug, titulo: post.titulo } : null}>
      <main className="container mx-auto px-4 py-16" data-testid="blog-post-page">
        <Link to="/blog" data-testid="blog-back-list" className="inline-flex items-center text-slate-500 hover:text-blue-600 text-sm mb-6"><ArrowLeft className="h-4 w-4 mr-1" /> Todos los artículos</Link>
        {error && <div data-testid="blog-post-notfound" className="text-center py-20"><h1 className="text-3xl font-bold text-slate-800 mb-2">Artículo no encontrado</h1><p className="text-slate-500">Puede que haya sido movido o despublicado.</p></div>}
        {post && (
          <article className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-3"><CategoryBadge categoria={post.categoria} /><p className="text-sm text-slate-500">{formatPostDate(post.fecha_publicacion)} · {post.autor}</p></div>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight mb-6" data-testid="blog-post-title">{post.titulo}</h1>
            <p className="text-xl text-slate-600 mb-6">{post.resumen}</p>
            <div className="mb-8" data-testid="blog-post-share-top"><ShareButtons variant="light" url={shareUrl} text={`${post.titulo} — IwannaInsurance`} onShare={trackShare} /></div>
            {post.imagen_url && <img src={post.imagen_url} alt={post.titulo} className="w-full rounded-3xl shadow-xl mb-10 max-h-[460px] object-cover" />}
            <div className="prose prose-slate prose-lg max-w-none prose-headings:font-bold prose-a:text-blue-600 prose-strong:text-slate-900 prose-blockquote:border-blue-500" data-testid="blog-post-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.contenido}</ReactMarkdown>
            </div>
            <div className="mt-12 rounded-3xl bg-slate-900 text-white p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="blog-post-share-bottom">
              <div><p className="font-bold text-lg">¿Te fue útil? Compártelo</p><p className="text-slate-400 text-sm">Ayuda a otros viajeros a viajar protegidos.</p></div>
              <ShareButtons variant="dark" url={shareUrl} text={`${post.titulo} — IwannaInsurance`} onShare={trackShare} />
            </div>
          </article>
        )}
        {post && relacionados.length > 0 && (
          <section className="max-w-5xl mx-auto mt-20" data-testid="blog-related">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Artículos relacionados</h2>
            <div className="grid md:grid-cols-3 gap-6">{relacionados.map((p, i) => <PostCard key={p.id} post={p} index={`rel-${i}`} />)}</div>
          </section>
        )}
      </main>
    </Shell>
  );
};
